import { useCallback, useEffect, useState } from 'react';
import { iapService, IAPProduct } from '../../services/iapService';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { track } from '../../utils/track';

/**
 * Store purchase for the Pro offer (App Store / Google Play), shared by the
 * Upgrade page and the post-Pro-day offer so either can buy in one tap.
 * Extracted from IAPUpgradeView (2026-11-03); same calls and tracking as before.
 */
export function useProPurchase(src: string) {
  const { updateUser } = useAuth();
  const [products, setProducts] = useState<IAPProduct[]>([]);
  const [supported, setSupported] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ok = await iapService.initialize();
        if (!alive) return;
        setSupported(ok);
        if (ok) {
          const list = await iapService.getProducts();
          if (alive) setProducts(list);
        }
      } catch (e) {
        console.error('Failed to initialize IAP:', e);
        if (alive) setSupported(false);
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const refreshUser = useCallback(async () => {
    const fresh = await apiService.getMe();
    await updateUser(fresh.user);
  }, [updateUser]);

  const purchase = useCallback(async (productId: string) => {
    setLoading(true); setError(null); setSuccess(false);
    track('purchase_start', { method: 'iap', plan: productId, src });
    try {
      const result = await iapService.purchaseProduct(productId);
      if (result.success) {
        track('purchase_result', { ok: true, method: 'iap', plan: productId, src });
        setSuccess(true);
        await refreshUser();
      } else {
        track('purchase_result', { ok: false, method: 'iap', plan: productId, src, errorMsg: (result.message || '').slice(0, 120) });
        setError(result.message);
      }
    } catch (e: any) {
      track('purchase_result', { ok: false, method: 'iap', plan: productId, src, errorStage: 'exception', errorMsg: (e?.message || '').slice(0, 120) });
      setError(e?.message || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  }, [refreshUser, src]);

  const restore = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await iapService.restorePurchases();
      if (result.success) { setSuccess(true); await refreshUser(); } else setError(result.message);
    } catch (e: any) {
      setError(e?.message || 'Restore failed');
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  return { products, supported, ready, loading, error, success, purchase, restore };
}

export interface ProRecap { meals: number; photoMeals: number; days: number; labMarkers: number; supplements: number; workouts: number; questions: number }

/** What this person has already put in (GET /api/iap/recap). null until loaded or on failure. */
export function useProRecap(): ProRecap | null {
  const [recap, setRecap] = useState<ProRecap | null>(null);
  useEffect(() => {
    let alive = true;
    apiService.get('/iap/recap').then((r: any) => { if (alive) setRecap(r?.data?.data || r?.data || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return recap;
}
